import { UploadIcon, X } from 'lucide-react';
import pluralize from 'pluralize';
import { useRef, useState } from 'react';
import { toast } from 'sonner';

import { cn } from '@/lib/shadcn/utils';
import { useUploadCandidatesMutation } from '@/modules/candidates/mutations/useUploadCandidatesMutation';
import { useUploadFilesMutation } from '@/modules/files/mutations/useUploadFilesMutation';
import type { JobApi } from '@/modules/jobs/types';
import { Button } from '@/modules/ui/components/button/Button';
import {
  Item,
  ItemActions,
  ItemContent,
  ItemTitle,
} from '@/modules/ui/components/item/Item';
import { Paragraph } from '@/modules/ui/components/paragraph/Paragraph';
import { extractErrorMessage } from '@/utils/extractErrorMessage';

interface CvUploadFormProps {
  job: JobApi;
  onSuccess: () => void;
  onCancel?: () => void;
}

export const CvUploadForm = ({
  job,
  onSuccess,
  onCancel,
}: CvUploadFormProps) => {
  const [fileList, setFileList] = useState<File[]>([]);

  const fileInputRef = useRef<HTMLInputElement>(null);
  const [isDragging, setIsDragging] = useState(false);

  const uploadMutation = useUploadFilesMutation();
  const manualUploadMutation = useUploadCandidatesMutation();

  const filterPdfFiles = (files: File[]) => {
    const pdfFiles = files.filter((file) => {
      const isPDF =
        file.type === 'application/pdf' ||
        file.name.toLowerCase().endsWith('.pdf');

      if (!isPDF) {
        toast.error(
          `Invalid file type: ${file.name}. Only PDF files are allowed.`,
        );
        return false;
      }
      return true;
    });

    return pdfFiles;
  };

  const handleFileSelect = (event: React.ChangeEvent<HTMLInputElement>) => {
    const files = Array.from(event.target.files || []);

    const pdfFiles = filterPdfFiles(files);
    setFileList((prev) => [...prev, ...pdfFiles]);

    if (fileInputRef.current) {
      fileInputRef.current.value = '';
    }
  };

  const handleFileRemove = (index: number) => {
    setFileList((prev) => prev.filter((_, i) => i !== index));
  };

  const handleDragOver = (e: React.DragEvent) => {
    e.preventDefault();
    e.stopPropagation();
  };

  const handleDrop = (e: React.DragEvent) => {
    e.preventDefault();
    e.stopPropagation();

    const files = Array.from(e.dataTransfer.files);
    const pdfFiles = filterPdfFiles(files);

    setFileList((prev) => [...prev, ...pdfFiles]);
    setIsDragging(false);
  };

  const handleSubmit = () => {
    if (fileList.length === 0) {
      toast.error('Please select at least one file to upload');
      return;
    }

    uploadMutation.mutate(fileList, {
      onSuccess: (uploadedFiles) => {
        const fileIds = (uploadedFiles ?? []).map((file) => file.id);
        manualUploadMutation.mutate(
          {
            jobId: job.id,
            body: {
              linkedInProfileUrls: [],
              fileIds,
            },
          },
          {
            onSuccess: () => {
              toast.success(
                `Candidates have been uploaded. Uploaded ${fileIds.length} ${pluralize('file', fileIds.length)}`,
              );
              setFileList([]);
              onSuccess();
            },
            onError: (error) => {
              toast.error(
                `Candidates have not been uploaded: ${extractErrorMessage(error)}`,
              );
            },
          },
        );
      },
      onError: (error) => {
        toast.error(
          `Files have not been uploaded: ${extractErrorMessage(error)}`,
        );
      },
    });
  };

  return (
    <div className="space-y-6">
      <div
        className={cn(
          'cursor-pointer border-2 border-dashed transition-colors',
          isDragging
            ? 'border-primary bg-primary/5'
            : 'border-border hover:border-primary/50',
        )}
        onDragOver={(e) => {
          handleDragOver(e);
          setIsDragging(true);
        }}
        onDragLeave={() => setIsDragging(false)}
        onDrop={handleDrop}
        onClick={() => fileInputRef.current?.click()}
      >
        <div className="flex flex-col items-center justify-center gap-4 py-20">
          <UploadIcon
            className={cn(
              'h-8 w-8 transition-colors',
              isDragging ? 'text-primary' : 'text-muted-foreground',
            )}
          />
          <div className="flex flex-col gap-1 text-center">
            <p className="font-medium">
              Click or drag CVs in PDF format to this area
            </p>
            <Paragraph variant="caption" className="text-muted-foreground">
              {fileList.length} PDF {pluralize('file', fileList.length)}{' '}
              selected
            </Paragraph>
          </div>
        </div>
      </div>

      <input
        ref={fileInputRef}
        type="file"
        accept=".pdf"
        multiple
        onChange={handleFileSelect}
        className="hidden"
      />

      {fileList.length > 0 && (
        <div className="max-h-[200px] space-y-2 overflow-y-auto">
          {fileList.map((file, index) => (
            <Item key={`${file.name}-${index}`} variant="outline" size="sm">
              <ItemContent>
                <ItemTitle className="truncate">{file.name}</ItemTitle>
              </ItemContent>
              <ItemActions>
                <Button
                  type="button"
                  variant="ghost"
                  size="icon"
                  onClick={() => handleFileRemove(index)}
                  className="text-muted-foreground h-8 w-8 shrink-0"
                >
                  <X className="h-4 w-4" />
                </Button>
              </ItemActions>
            </Item>
          ))}
        </div>
      )}

      <div className="flex justify-end gap-2">
        {onCancel && (
          <Button type="button" variant="outline" onClick={onCancel}>
            Cancel
          </Button>
        )}
        <Button
          type="button"
          onClick={handleSubmit}
          loading={uploadMutation.isPending || manualUploadMutation.isPending}
        >
          Upload
        </Button>
      </div>
    </div>
  );
};
